import { useState } from 'react';
import { Check, ChevronDown, Shield } from 'lucide-react';
import { roleLabel } from '@/lib/format';
import { viewsFor } from '@/lib/views';
import { cn } from '@/lib/cn';
import { strings } from '@/strings';
import type { Role } from '@/types/roles';

/**
 * Switches which of the signed-in person's own trees they are looking at.
 *
 * Renders nothing at all when there is only one — which is everyone except an
 * admin today. A switcher offering a single choice is a control that cannot do
 * anything, and it teaches people that the bar is full of dead buttons.
 *
 * It sits in the top bar rather than inside the user menu because it changes
 * what the whole screen shows; burying a navigation-wide control two clicks
 * deep is the mistake Saleem's own layout notes call out.
 */
export function RoleSwitcher({
  roles,
  view,
  onChange,
}: {
  roles: Role[];
  view: Role;
  onChange: (next: Role) => void;
}) {
  const [open, setOpen] = useState(false);
  const views = viewsFor(roles);
  if (views.length < 2) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={strings.viewSwitch}
        aria-expanded={open}
        className="flex min-h-11 items-center gap-1.5 rounded-md border border-border-subtle px-3 py-1.5 text-sm text-ink transition-colors hover:bg-surface"
      >
        <Shield className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
        <span className="hidden sm:inline">{roleLabel(view)}</span>
        <ChevronDown
          className={cn('h-3 w-3 shrink-0 text-muted transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <>
          <div data-testid="view-switcher-backdrop" className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-2 w-56 rounded-lg border border-border-subtle bg-white py-1 shadow-lg rtl:left-0 ltr:right-0">
            {views.map((candidate) => (
              <button
                key={candidate}
                type="button"
                onClick={() => { onChange(candidate); setOpen(false); }}
                className={cn(
                  'flex min-h-11 w-full items-center justify-between gap-2 px-4 py-2 text-start text-sm hover:bg-surface',
                  candidate === view ? 'font-semibold text-primary' : 'text-ink'
                )}
              >
                <span>{roleLabel(candidate)}</span>
                {candidate === view ? <Check className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
